import { getAutoProps } from 'js/auto_props/client';

import { recordExtensionBucketing } from 'js/bulk_download/autotest/recordExtensionBucketing';

/**
 * The reader's arm of the post-pack fake door, or null for no funnel — meaning
 * the PDF Pack download shows the stock progress modal.
 *
 * The arm comes from the SERVER, in the `BulkDownloadFlags` auto-prop, because
 * the client-side `abTest()` helper has no rollout gate: it would bucket every
 * pack downloader the moment this ships, and this funnel replaces a screen
 * someone has just paid for. The server honours the staged rollout, so exposure
 * starts at nobody and is ramped deliberately.
 *
 * A PROP RATHER THAN AN ENDPOINT, which this used to be. `showBulkDownloadModal`
 * gates rendering the download modal on knowing the arm, so a fetch put a
 * network round trip in front of a paid-for download — bounded by a timeout
 * that still cost the reader up to a second of dead screen, and unbounded
 * before that. Reading a prop is synchronous, so that whole class of problem is
 * gone along with the endpoint, its controller and its route.
 *
 * The server resolves READ-ONLY on page render, so loading a page enrols
 * nobody; the bucketing is recorded here, at the download click, which is the
 * exposure the readout counts. `control` and `holdout` record and then yield no
 * funnel — control needs a denominator as much as the arms do.
 */
const TEST_KEY =
  'demo_day_2026_q3_reference_manager_and_chrome_extension_fake_door';

/**
 * Read the server's decision without `getAutoProps`' console noise on pages
 * that never registered these flags — which is most of them, and is not an
 * error. Mirrors `quietExtensionVariant` and `bulkDownloadTrialPrice`.
 */
function quietFlags(): { postPackVariant?: string; postPackForced?: boolean } {
  try {
    return getAutoProps('BulkDownloadFlags') ?? {};
  } catch {
    return {};
  }
}

export function referenceManagerFunnelArm(): string | null {
  const { postPackVariant: variant, postPackForced: forced } = quietFlags();
  if (!variant) return null;

  /*
   * A forced arm is shown but never recorded. The `?ab_test[...]` override
   * rides on the page request, so this POST carries none of it: the server
   * would find no override, pass the rollout gate, disagree with the forced
   * arm, raise a Sentry variant mismatch, and write a user_tests row for
   * traffic the readout excludes by joining to that very table.
   */
  if (!forced) recordExtensionBucketing(TEST_KEY, variant);

  if (variant === 'control' || variant === 'holdout') return null;
  return variant;
}
